import { gql } from '@apollo/client';
import { HierarchyLevel } from './types';

/**
 * Max application IDs per batch request
 */
export const MAX_APP_IDS_PER_BATCH = 100;

/**
 * Query to fetch applications managed by the user and their organizational hierarchy
 * This query uses the existing applicationPortfolio which fetches apps owned by
 * the user and their direct reports from the user_hierarchy table.
 *
 * For CGA audit purposes, this provides a point-in-time snapshot of asset inventory.
 */
export const CGAHierarchyAppsQuery = gql`
  query CGAHierarchyApps(
    $login: String!
    $pagination: PaginationInput
    $hierarchyLevel: HierarchyLevel
  ) {
    identity {
      user(login: $login) {
        login
        displayName
        title
        department
        resources {
          applicationPortfolio(
            pagination: $pagination
            hierarchyLevel: $hierarchyLevel
          ) {
            applications {
              applicationId
              name
              description
              status
              appGroup
              serviceLevelName
              serviceLevel
              serviceDomain
              createdAt
              updatedAt
              owner {
                login
                displayName
                title
                department
              }
            }
            totalCount
          }
        }
      }
    }
  }
`;

/**
 * Helper to generate the GraphQL query string for audit evidence
 * @param login - The login of the user for whom the report is generated
 * @param hierarchyLevel - The hierarchy level used in the query (direct reports vs full hierarchy)
 * @returns A formatted string describing the queries and data sources used
 */
export const getQueryStringForAudit = (
  login: string,
  hierarchyLevel: HierarchyLevel,
): string => {
  return `
CGA Compliance Report - Query Details
=====================================
Target User: ${login}
Hierarchy Level: ${hierarchyLevel}

STEP 1: Fetch Applications
--------------------------
Query: CGAHierarchyAppsQuery
Variables: {
  login: "${login}",
  pagination: { limit: 100, offset: 0 },
  hierarchyLevel: "${hierarchyLevel}"
}

STEP 2: Fetch Resources (on Export)
-----------------------------------
Query: ApplicationResourcesQuery
Variables: {
  filter: { applicationIds: [<app IDs from step 1>] },
  devicesPagination: { limit: 100, offset: 0 }
}
Max 100 application IDs per request (paginated)

STEP 3: Fetch CNAP Data (on Export)
-----------------------------------
REST Endpoint: /api/cnap/cga-audit/batch
Payload: {
  applicationIds: [<app IDs from step 1>]
}

Data Sources:
- Applications: IOP ITRC Database (applicationPortfolio)
- Devices: IOP CMDB (bulk query with IN clause)
- Cloud Accounts: Neo4j (batch query by app IDs)
- CNAP Tenancies: Neo4j (batch query by app IDs)

Note: applicationPortfolio returns applications based on the hierarchyLevel parameter:
- DIRECT_REPORTS: Apps owned by the user and their direct reports only (level=0)
- FULL_REPORTING_HIERARCHY: Apps owned by the user and all members in the reporting hierarchy
  `.trim();
};

/**
 * Application Resources Query using applicationsPaged
 *
 * This query fetches resources (devices, cloud accounts)
 * for a batch of application IDs using the existing applicationsPaged query.
 * CNAP data is fetched separately via REST API.
 *
 * Features:
 * - Uses applicationsPaged with applicationIds filter
 * - Fetches devices and cloud accounts in a single query
 * - CNAP data fetched via REST API (/api/cnap/cga-audit/{appId})
 *
 * Performance improvement:
 * - Before: 3 queries per app (N*3 queries)
 * - After: 1 applicationsPaged query + N CNAP REST calls
 */
export const ApplicationResourcesQuery = gql`
  query ApplicationResources(
    $filter: ResourceCatalogApplicationFilter
    $devicesPagination: PaginationInput
  ) {
    resources {
      applicationsPaged(filter: $filter) {
        totalCount
        applications {
          applicationId
          name
          status
          owner {
            displayName
          }
          cloudAccounts {
            id
            name
            platform
            status
            environment
          }
          devicesPaged(pagination: $devicesPagination) {
            devices {
              tier
              ipAddress
              hostname
              environment
              type
              fqdn
              href
              operationalStatus
              serialNumber
              make
              model
              osInfo {
                make
                name
                edition
                release
                version
              }
            }
            totalCount
          }
        }
      }
    }
  }
`;

/**
 * Helper to generate the query string for audit evidence (resources)
 * @param login - The login of the user for whom the report is generated
 * @param hierarchyLevel - The hierarchy level used in the initial query (for context)
 * @param applicationCount - The number of applications for which resources are fetched
 * @returns A formatted string describing the queries and data sources used for resource fetching
 */
export const getQuerySignatureForAudit = (
  login: string,
  hierarchyLevel: HierarchyLevel,
  applicationCount: number,
): string => {
  return `
CGA Compliance Report Query
============================
Target User: ${login}
Hierarchy Level: ${hierarchyLevel}
Applications: ${applicationCount}

GraphQL Queries:
1. CGAHierarchyAppsQuery - Fetch applications from portfolio
2. ApplicationResourcesQuery - Fetch devices and cloud accounts via applicationsPaged

REST API:
- CNAP Data: /api/cnap/cga-audit/{appId}

Data Sources:
- Applications: IOP ITRC Database (applicationPortfolio)
- Devices: IOP CMDB (via applicationsPaged.devicesPaged)
- Cloud Accounts: Neo4j (via applicationsPaged.cloudAccounts)
- CNAP: External REST API
`.trim();
};
